import {
	Button,
	Divider,
	type DropdownItemProps,
	Form,
	Header,
	Label,
	Message,
	Modal,
	Segment,
} from '@mairistem/composants';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { isEmpty } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type { DropdownProps } from 'semantic-ui-react';
import UtilisateurSelectedForm from '../UtilisateurSelectedForm';
import './UtilisateurReplaceModal.css';
import DemandeStatusEnum from '../../../enums/demandeStatusEnum';
import UtilisateurError from '../../../enums/UtilisateurError';
import type { DemandeCompte } from '../../../interfaces/demandeCompte';
import type {
	UtilisateurCollectivite,
	UtilisateurCollectiviteToCreate,
} from '../../../interfaces/utilisateurCollectivite';
import { updateDemandeCompte } from '../../../services/demandeCompteService';
import getBureauxUtilisateur from '../../../utils/getBureauxUtilisateur';

interface UtilisateurReplaceModalProps {
	demandesComptes: DemandeCompte[];
	selectedUtilisateur: UtilisateurCollectivite;
	onClose: () => void;
	onValidate: (demandeDeCompteId: number) => Promise<void>;
	isLoading?: boolean;
	onUpdateDemandeCompte: (updatedDemandeCompte: DemandeCompte) => void;
}

const confirmationTextRequired = 'Remplacer cet utilisateur';

// Fonction de transformation DemandeCompte vers UtilisateurCollectiviteToCreate
const transformDemandeCompteToUtilisateur = (
	demandeCompte: DemandeCompte,
): UtilisateurCollectiviteToCreate => ({
	email: demandeCompte.email || '',
	nom: demandeCompte.nom || '',
	prenom: demandeCompte.prenom || '',
	qualite: demandeCompte.qualite || '',
	codePostal: demandeCompte.codePostal || '',
	ville: demandeCompte.ville || '',
	collectivites: [], // Les collectivités seront héritées de l'utilisateur remplacé
});

const UtilisateurReplaceModal = ({
	selectedUtilisateur,
	demandesComptes,
	onClose,
	onValidate,
	isLoading,
	onUpdateDemandeCompte,
}: UtilisateurReplaceModalProps) => {
	const [demandeCompteOptions, setDemandeCompteOptions] = React.useState<
		DropdownItemProps[]
	>([]);
	const [selectedMickUser, setSelectedMickUser] =
		React.useState<DemandeCompte | null>(null);
	const [formData, setFormData] =
		React.useState<UtilisateurCollectiviteToCreate | null>(null);
	const [errors, setErrors] = React.useState<UtilisateurError[]>();
	const [isMickUserDirty, setIsMickUserDirty] = React.useState(false);
	const bureaux: string[] = React.useMemo(() => {
		if (!selectedMickUser?.siren) return [];
		const filteredUtilisateur = {
			...selectedUtilisateur,
			collectivites: selectedUtilisateur.collectivites.filter(
				(c) => c.siren === selectedMickUser.siren,
			),
		};
		return getBureauxUtilisateur(filteredUtilisateur);
	}, [selectedUtilisateur, selectedMickUser]);
	const [isCertificatRequired, setIsCertificatRequired] = React.useState(true);
	const [confirmationText, setConfirmationText] = React.useState('');

	const handleChangeMickUser = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ value }: DropdownProps,
	) => {
		setConfirmationText('');
		setIsMickUserDirty(false);
		setErrors([]);
		const newSelectedUser = demandesComptes.find(
			(demande: DemandeCompte) => demande.identifiant === value,
		);
		setSelectedMickUser(newSelectedUser || null);

		// Transformer les données pour le formulaire
		if (newSelectedUser) {
			setFormData(transformDemandeCompteToUtilisateur(newSelectedUser));
		} else {
			setFormData(null);
		}
	};

	// Permet de modifier les informations dans le formulaire
	const handleUpdateFormData = (
		updatedData: Partial<UtilisateurCollectiviteToCreate>,
	) => {
		setConfirmationText('');
		setIsMickUserDirty(true);
		setFormData(updatedData as UtilisateurCollectiviteToCreate);

		// Synchroniser avec selectedMickUser pour la compatibilité
		if (selectedMickUser) {
			const updatedMickUser: DemandeCompte = {
				...selectedMickUser,
				email: updatedData.email || selectedMickUser.email,
				nom: updatedData.nom || selectedMickUser.nom,
				prenom: updatedData.prenom || selectedMickUser.prenom,
				qualite: updatedData.qualite || selectedMickUser.qualite,
				codePostal: updatedData.codePostal || selectedMickUser.codePostal,
				ville: updatedData.ville || selectedMickUser.ville,
			};
			setSelectedMickUser(updatedMickUser);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setConfirmationText(e.target.value);
	};

	const handleCheckData = () => {
		const dataErrors: UtilisateurError[] = [];
		if (!formData) return false;

		if (isEmpty(formData.nom)) {
			dataErrors.push(UtilisateurError.NOM);
		}
		if (isEmpty(formData.email)) {
			dataErrors.push(UtilisateurError.EMAIL);
		}
		if (isEmpty(formData.prenom)) {
			dataErrors.push(UtilisateurError.PRENOM);
		}

		if (
			isEmpty(confirmationText) ||
			confirmationText.toUpperCase() !== confirmationTextRequired.toUpperCase()
		) {
			dataErrors.push(UtilisateurError.CONFIRMATION);
		}

		setErrors(dataErrors);

		if (!isEmpty(dataErrors)) {
			toast.error(
				'Certaines informations sont manquantes. Merci de les compléter.',
			);
			return false;
		}
		return true;
	};

	React.useEffect(() => {
		const newDemandeCompte: DropdownItemProps[] = [];
		const utilisateurSirens = selectedUtilisateur.collectivites.map(
			(c) => c.siren,
		);
		demandesComptes.forEach((demande) => {
			if (
				demande.statusCode === DemandeStatusEnum.OK ||
				demande.statusCode === DemandeStatusEnum.ABANDON ||
				demande.statusCode === DemandeStatusEnum.TRAITEE ||
				demande.statusCode === DemandeStatusEnum.HS ||
				demande.statusCode === DemandeStatusEnum.EN_COURS
			) {
				return;
			}

			if (
				isCertificatRequired &&
				(!demande.certificat ||
					!demande.certificatExpire ||
					new Date(demande.certificatExpire).getTime() < Date.now())
			) {
				return;
			}

			if (demande.siren && !utilisateurSirens.includes(demande.siren)) {
				return;
			}

			newDemandeCompte.push({
				key: demande.identifiant,
				text: `${demande.prenom} ${demande.nom} (Code client: ${demande.codeClient})`,
				value: demande.identifiant,
				'data-testid': `user-option-${demande.identifiant}`,
			});
		});

		setDemandeCompteOptions(newDemandeCompte);
	}, [demandesComptes, isCertificatRequired, selectedUtilisateur]);

	const handleValidate = async () => {
		if (!handleCheckData()) {
			return;
		}

		if (selectedMickUser) {
			if (isMickUserDirty) {
				try {
					await updateDemandeCompte(selectedMickUser);
					onUpdateDemandeCompte(selectedMickUser);
					toast.success(
						'La demande de compte a été modifiée avec succès. Les utilisateurs vont être tenus informés par mail.',
					);
				} catch (error) {
					toast.error(
						getErrorMessage(
							error,
							'Erreur lors de la modification de la demande de compte.',
						),
					);
					return;
				}
			}
			await onValidate(selectedMickUser.identifiant);
		}
	};

	React.useEffect(() => {
		const isRequiredCertificat = selectedUtilisateur?.collectivites?.some(
			(c) => !!c.loginSlow,
		);
		setIsCertificatRequired(isRequiredCertificat);
	}, [selectedUtilisateur]);

	return (
		<Modal open onClose={onClose} closeIcon>
			<Modal.Header>Remplacer un utilisateur</Modal.Header>
			<Modal.Content>
				<Modal.Description>
					<Message info className="message-replace-user">
						<Message.Header>Utilisateur sortant :</Message.Header>
						<div>
							<strong>Nom: </strong>
							{selectedUtilisateur?.prenom} {selectedUtilisateur?.nom}
						</div>
						<div>
							<strong>Login: </strong>
							{selectedUtilisateur?.login}
						</div>
						<div>
							<strong>Entités: </strong>
							{selectedUtilisateur?.collectivites.length > 0
								? selectedUtilisateur.collectivites
										.map((collectivite) => collectivite.libelle)
										.join(', ')
								: 0}
						</div>
					</Message>

					<Segment>
						<Header as="h4">
							Sélectionner depuis &quot;Mes Informations Client&quot;
						</Header>
						{demandeCompteOptions.length === 0 ? (
							<Message warning>
								Aucune demande de compte disponible pour le remplacement.
							</Message>
						) : (
							<Combobox
								options={demandeCompteOptions}
								placeholder="Sélectionnez un utilisateur"
								selection
								fluid
								value={selectedMickUser?.identifiant}
								onChange={handleChangeMickUser}
							/>
						)}
						{selectedMickUser && formData && (
							<>
								<Divider hidden />
								<UtilisateurSelectedForm
									user={formData}
									onChange={handleUpdateFormData}
									errors={errors}
								/>
							</>
						)}
					</Segment>

					{selectedMickUser && (
						<Segment basic>
							<Header textAlign="left" as="h4">
								Bureaux associés
							</Header>
							{bureaux.length === 0 ? (
								<Message info>Aucun bureau associé à cet utilisateur.</Message>
							) : (
								bureaux.map((bureau) => (
									<Label key={bureau} className="bureau-user" circular basic>
										{bureau}
									</Label>
								))
							)}
						</Segment>
					)}
					<Segment basic>
						{!selectedMickUser ? (
							<Message error>Veuillez sélectionner un utilisateur</Message>
						) : (
							<div>
								<p>
									Demande de remplacement de {selectedUtilisateur?.prenom}{' '}
									{selectedUtilisateur?.nom} par {formData?.prenom}{' '}
									{formData?.nom}
									<br />
									Veuillez saisir <strong>Remplacer cet utilisateur</strong> en
									toutes lettres pour confirmer cette action.
									<br />
								</p>

								<Form>
									<Form.Input
										fluid
										required
										value={confirmationText}
										onChange={handleChange}
										error={errors?.includes(UtilisateurError.CONFIRMATION)}
										placeholder="Veuillez saisir 'Remplacer cet utilisateur' pour confirmer le remplacement"
									/>
								</Form>
							</div>
						)}
					</Segment>
				</Modal.Description>
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose}>Annuler</Button>
				<Button
					primary
					onClick={handleValidate}
					loading={isLoading}
					disabled={!selectedMickUser}
					className={!selectedMickUser ? 'button-disabled-replace-user' : ''}
				>
					Remplacer l&apos;utilisateur
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default UtilisateurReplaceModal;
